import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import vehicleService from '../../../services/vehicleService';
import VehicleAboutSection from '../components/VehicleAboutSection';
import { Vehicle } from '../../../types/Vehicle';
import { usePermissions } from '../../../hooks/usePermissions';
import { Alert, Button } from '@/components/ui';

const VehicleCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreateVehicle = hasPermission('vehicle:create');
  const canEditVehicle = hasPermission('vehicle:edit');
  const [isCreating, setIsCreating] = useState(false);
  // Save failures render inline above the form. window.alert blocked the page and vanished on
  // dismissal, leaving no record of why the create was refused.
  const [createError, setCreateError] = useState<string | null>(null);

  // Create a blank vehicle object for the form
  const blankVehicle: Partial<Vehicle> = {
    unitNumber: '',
    status: 'ACTIVE',
    vehicleServiceAreas: []
  };

  const handleCreate = async (vehicleData: Partial<Vehicle>) => {
    if (!canCreateVehicle) {
      setCreateError('You do not have permission to create vehicles');
      return;
    }

    try {
      setCreateError(null);
      setIsCreating(true);
      const createdVehicle = await vehicleService.createVehicle(vehicleData);
      // Navigate to the detail page of the newly created vehicle
      navigate(`/vehicles/${createdVehicle.id}`);
    } catch (error) {
      console.error('Error creating vehicle:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create vehicle');
    } finally {
      setIsCreating(false);
    }
  };

  if (!canCreateVehicle) {
    return (
      <div className="tw-w-full tw-p-4">
        <Alert variant="danger">
          You do not have permission to create vehicles
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/vehicles')}>
          Back to Vehicles
        </Button>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-p-4">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h1>Create New Vehicle</h1>
        <Button variant="secondary" onClick={() => navigate('/vehicles')}>
          Cancel
        </Button>
      </div>

      {createError && (
        <Alert variant="danger" role="alert" className="tw-mb-3">
          {createError}
        </Alert>
      )}

      <VehicleCreateForm 
        initialData={blankVehicle}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        canEdit={canEditVehicle}
      />
    </div>
  );
};

// Create form component that wraps VehicleAboutSection for create mode
interface VehicleCreateFormProps {
  initialData: Partial<Vehicle>;
  onSubmit: (data: Partial<Vehicle>) => Promise<void>;
  isSubmitting: boolean;
  canEdit: boolean;
}

const VehicleCreateForm: React.FC<VehicleCreateFormProps> = ({ 
  initialData, 
  onSubmit, 
  isSubmitting,
  canEdit 
}) => {
  const [formData, setFormData] = useState<Partial<Vehicle>>(initialData);
  const [latestEditedVehicle, setLatestEditedVehicle] = useState<Partial<Vehicle>>(initialData);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = async (createData: Partial<Vehicle>) => {
    // Validate required fields
    if (!createData.unitNumber || createData.unitNumber.trim() === '') {
      setValidationError('Unit Number is required.');
      return;
    }

    setValidationError(null);
    await onSubmit(createData);
  };

  const handleCancel = () => {
    // Reset form data
    setFormData(initialData);
    setLatestEditedVehicle(initialData);
  };

  // Convert partial vehicle to full Vehicle for VehicleAboutSection
  // VehicleAboutSection expects a Vehicle object, so we'll create a minimal one
  // Helper to convert null/empty to undefined for optional fields
  const toUndefined = <T,>(value: T | null | undefined): T | undefined => {
    return value ?? undefined;
  };

  const vehicleForDisplay: Vehicle = {
    id: 0,
    unitNumber: formData.unitNumber || '',
    timberlineUnitNumber: toUndefined(formData.timberlineUnitNumber),
    description: toUndefined(formData.description),
    status: formData.status || 'ACTIVE',
    numberOfAxles: toUndefined(formData.numberOfAxles),
    year: toUndefined(formData.year),
    rate: toUndefined(formData.rate),
    vehicleType: toUndefined(formData.vehicleType),
    vehicleClassification: toUndefined(formData.vehicleClassification),
    trailerOrAttachment: toUndefined(formData.trailerOrAttachment),
    fuelType: toUndefined(formData.fuelType),
    brakeTypeFront: toUndefined(formData.brakeTypeFront),
    brakeTypeRear: toUndefined(formData.brakeTypeRear),
    hasGPS: toUndefined(formData.hasGPS),
    canPatrol: toUndefined(formData.canPatrol),
    comments: toUndefined(formData.comments),
    imageUrl: toUndefined(formData.imageUrl),
    currentStatus: toUndefined(formData.currentStatus),
    currentOdometer: toUndefined(formData.currentOdometer),
    lastOdometerReportedType: toUndefined(formData.lastOdometerReportedType),
    lastOdometerReportedDateTime: toUndefined(formData.lastOdometerReportedDateTime),
    nextPMDue: toUndefined(formData.nextPMDue),
    nextCVIP: toUndefined(formData.nextCVIP),
    nextPMType: toUndefined(formData.nextPMType),
    nextMinor: toUndefined(formData.nextMinor),
    nextMajor: toUndefined(formData.nextMajor),
    nextGreasing: toUndefined(formData.nextGreasing),
    estimatedReturnToServiceDate: toUndefined(formData.estimatedReturnToServiceDate),
    parkedReady: toUndefined(formData.parkedReady),
    lastParked: toUndefined(formData.lastParked),
    vehicleProfileId: toUndefined(formData.vehicleProfileId),
    vehicleProfile: undefined,
    vehicleServiceAreas: formData.vehicleServiceAreas || [],
    vinNumber: toUndefined(formData.vinNumber),
    make: toUndefined(formData.make),
    model: toUndefined(formData.model),
    summerConfiguration: toUndefined(formData.summerConfiguration),
    winterConfiguration: toUndefined(formData.winterConfiguration),
    spreaderSystemType: toUndefined(formData.spreaderSystemType),
    warrantyStatus: toUndefined(formData.warrantyStatus),
    registrationNumber: toUndefined(formData.registrationNumber),
    tareWeight: toUndefined(formData.tareWeight),
    wheelbase: toUndefined(formData.wheelbase),
    gvwr: toUndefined(formData.gvwr),
    height: toUndefined(formData.height),
    length: toUndefined(formData.length),
    width: toUndefined(formData.width),
    calculatedCenterOfGravity: toUndefined(formData.calculatedCenterOfGravity),
    currentLocation: toUndefined(formData.currentLocation),
    ownershipStatus: toUndefined(formData.ownershipStatus),
    homeYardId: toUndefined(formData.homeYardId),
    currentYardId: toUndefined(formData.currentYardId),
    homeYard: undefined,
    currentYard: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  return (
    <div>
      {validationError && (
        <Alert variant="danger" role="alert" className="tw-mb-3">
          {validationError}
        </Alert>
      )}
      <VehicleAboutSection
        vehicle={vehicleForDisplay}
        onUpdate={(updatedVehicle) => {
          // Update form data when vehicle is updated
          const updatedData: Partial<Vehicle> = {
            ...updatedVehicle,
            vehicleServiceAreas: updatedVehicle.vehicleServiceAreas || []
          };
          setFormData(updatedData);
          setLatestEditedVehicle(updatedData);
        }} 
        canEdit={canEdit}
        isCreateMode={true}
        onSave={handleSave}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default VehicleCreatePage;
